/** Your ONE Profile — compact customer-facing card on Customer Home.
 *  Fetches the customer's playbook (safe subset) and renders the top
 *  operational profile the customer needs to know.  Sensitive IDs are
 *  redacted server-side. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Section from "@/components/app/Section";
import { ArrowRight, ShieldCheck } from "lucide-react";

const MDM_LABEL = {
  intune: "Microsoft Intune", jamf_pro: "Jamf Pro", kandji: "Kandji", mosyle: "Mosyle",
  workspace_one: "Workspace ONE", soti: "SOTI MobiControl", google_endpoint: "Google Endpoint",
};

const LABELISE = {
  yes: "Enabled", no: "Not enabled", unknown: "—", not_applicable: "N/A",
  allowed: "Allowed", not_allowed: "Not allowed", approval_required: "Approval required",
};

function labelize(v, isMdm = false) {
  if (isMdm) return MDM_LABEL[v] || v || "—";
  return LABELISE[v] || v || "—";
}

export default function YourOneProfile() {
  const { user } = useAuth();
  const [pb, setPb] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user?.master_company_id) { setLoaded(true); return; }
    let cancel = false;
    api.get(`/customer-playbook/${user.master_company_id}/playbook`)
      .then(({ data }) => !cancel && setPb(data))
      .catch(() => {})
      .finally(() => !cancel && setLoaded(true));
    return () => { cancel = true; };
  }, [user]);

  if (!loaded || !pb || pb.exists === false) return null;

  const tiles = [
    { label: "Apple Business Manager", value: labelize(pb.apple?.abm_enabled) },
    { label: "MDM", value: labelize(pb.apple?.primary_mdm || pb.windows?.primary_mgmt_platform, true) },
    { label: "Windows Autopilot", value: labelize(pb.windows?.autopilot_enabled) },
    { label: "AppleCare+", value: labelize(pb.apple?.applecare_plus) },
    { label: "Warehouse", value: pb.warehouse?.suntronix_warehousing === "yes" ? "Managed by Suntronix" : labelize(pb.warehouse?.suntronix_warehousing) },
    { label: "Reuse", value: labelize(pb.reuse?.policy) },
    { label: "Recovery", value: labelize(pb.recovery?.enabled) },
    { label: "Data Erasure", value: labelize(pb.erasure?.required) },
  ];

  return (
    <Section
      title="Your ONE Profile"
      hint="How your technology is set up."
      action={
        <Link
          to={`/customer/${user.master_company_id}/tech-env`}
          className="st-btn st-btn-ghost st-btn-sm"
          data-testid="your-one-profile-view-all"
        >
          View technology environment <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.6} />
        </Link>
      }
      testId="your-one-profile"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {tiles.map((t) => (
          <div key={t.label} className="st-panel p-3 rounded-md flex items-start gap-2" data-testid={`your-one-profile-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--st-accent)] mt-0.5 shrink-0" strokeWidth={1.8} />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)] truncate">{t.label}</div>
              <div className="text-sm font-medium text-[color:var(--st-text)] mt-0.5 truncate">{t.value}</div>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}
